import { userBasicInfoPublicPages } from 'modules/graphqlQueries';
import { Heading, Title, Wrapper } from 'styles/terms-and-conditions.styles';

import Layout from '../components/layout/Layout';
import { useLoadData } from '../modules/useLoadData';
import { UserType } from '../types/UserTypes';

const getQuery = (userId) => `{
  user(_id: "${userId}") {
    ${userBasicInfoPublicPages}
  }
}`;

const PrivacyPolicy = (): JSX.Element => {
  const {
    isLoading,
    response: {
      data: { user },
    },
  } = useLoadData(getQuery);

  return (
    <Layout user={user as UserType} isLoading={isLoading}>
      <Wrapper>
        <Title>Platform Privacy Policy - GDPR</Title>
        <ol>
          <Heading id="general-information">
            <a href="privacy-policy#general-information">
              <li>
                General Information – Our contact details as data controllers
              </li>
            </a>
          </Heading>
          <p>
            In this Privacy Policy, bid2board PC, a private limited company
            organized and existing under the laws of Greece, whose registered
            office is at 2 Afentouli Street, Piraeus, 18536, Greece (
            <strong>“bid2board”</strong>), states its practices regarding
            information that it collects from you or through your use of the
            website hosting the Platform, which is accessible via
            https://.bid2board.com (<strong>“Website”</strong>), how that
            information may be used and with whom it may be shared (the{' '}
            <strong>“Privacy Policy”</strong>). User’s privacy is bid2board’s
            top priority. bid2board may use Personal Information and/or Cookies,
            as defined below, for the purpose of making the Website and/or
            Platform available to you in accordance with the Agreement. The data
            controller of your Personal Information is bid2board PCof 2
            Afentouli Street, Piraeus, 18536, Greece.
          </p>
          <p>
            The Privacy Policy will be revised from time to time. bid2board will
            notify you of any revisions by posting the updated version of the
            Privacy Policy on the Website and/or the Platform. Revised terms
            become effective on the first date that you access or use the
            Website and/or Platform after the updated Privacy Policy has been
            posted. bid2board advises you to periodically check the Privacy
            Policy for any such modifications or revisions. You declare that you
            have read and understood the Privacy Policy.
          </p>
          <p>
            The Website and/or Platform may include links to third-party
            websites whose privacy policies may differ from ours. Any
            information that these third parties collect about you is governed
            by their respective privacy policies. We suggest that you contact
            those third parties directly in order to obtain information about
            their privacy policies.
          </p>
          <p>
            Unless otherwise provided herein, defined terms shall have the
            meaning ascribed to them in the Terms of Use. The Terms of Use and
            the Privacy Policy constitute the entire agreement between you and
            bid2board (the <strong>“Agreement”</strong>). For the purposes of
            this Privacy Policy, <strong>“use”</strong> shall mean any operation
            or set of operations performed on information provided by you, such
            as collection, use, storage, forwarding and/or disclosure.{' '}
            <strong>“Process”</strong>
            shall be construed accordingly.
          </p>
          <p>
            For purposes of the present Privacy Policy, “personal information”,
            “processing”, “use”, “data controller”, “personal data breach”,
            “restriction of processing” shall have the same meaning as those
            specified by the “General Data Protection Regulation” (hereinafter
            referred to as <strong>“GDPR”</strong>).
          </p>
          <p>
            In case you wish to address any questions regarding the present
            Privacy Policy, you may contact <strong>Andreas Zompanakis</strong>,
            by email in the following e-mail address{' '}
            <strong>info@bid2board.com</strong>.
          </p>
          <Heading id="type-of-personal-information-we-collect">
            <a href="privacy-policy#type-of-personal-information-we-collect">
              <li>Type of Personal Information we collect</li>
            </a>
          </Heading>
          <p>
            <strong>“Personal Information”</strong> refers to information that
            allows bid2board to identify you, directly or indirectly, in
            particular by reference to an identifier, in the course of your
            registering to, accessing and using the Website and/or Platform. You
            shall not provide any Personal Information about others unless you
            are authorized to do so. By submitting Personal Information about
            others, you represent and warrant that you are authorized to do so
            and that you have received authorization from the person about whom
            you provide Personal Information and, where required, that this
            person has consented to have all such Personal Information submitted
            in accordance with this Privacy Policy.
          </p>
          <p>
            When accessing and using the Website and the Platform through a paid
            account Personal Information shall be provided by you for the
            purposes of registration to the Platform and account creation; it
            includes name, address, email, telephone number, company/employer,
            job title, job department, payment details. Failure to provide such
            Personal Information will result in your inability to register to,
            access, and use the Platform and its subsequent versions.
          </p>
          <p>
            bid2board may collect Personal Information automatically through
            your use of the Website and/or the Platform, such as IP address,
            date and daytime of access request, time zone difference to
            Greenwich Mean Time (GMT), the specific site requested, HTTP-status
            code, amount of data transmitted, the website from which you visit
            the Website, browser type, operating system, and user interface,
            language, and version of the browser software. Please refer to the
            section on Cookies and similar technologies below for more
            information.
          </p>
          <Heading id="purposes-for-which-your-personal-information-is-processed">
            <a href="privacy-policy#purposes-for-which-your-personal-information-is-processed">
              <li>Purposes for which your Personal Information is processed</li>
            </a>
          </Heading>
          <p>bid2board may use your Personal Information to:</p>
          <ol>
            <li>
              Process your registration for the purposes of using the Platform,
            </li>
            <li>
              Provide you with the services and products of the Platform,
              including but not limited to process payments, if applicable, and
              provide you with customer support,
            </li>
            <li>
              Communicate with you with respect to the use of the Platform
              and/or our services and products,
            </li>
            <li>
              Notify you of any modifications/revisions to the Platform’s Terms
              of Use, including but not limited to the present Privacy Policy,
            </li>
            <li>Ensure compliance with our legal obligations,</li>
            <li>
              Improve the quality of bid2board’s collaborations and provision of
              services and our unimpeded and effective communication and
              interaction with you,
            </li>
            <li>Establish, exercise, or support our legal claims,</li>
            <li>
              Improve your interaction with the Platform and optimize your
              experience with it,
            </li>
            <li>
              Request your participation in surveys related to the Platform, its
              services, and products,
            </li>
            <li>
              Protect our network, devices, and programs for cyber-security
              (i.e. ensuring confidentiality, integrity and availability of the
              information, data from attacks or unauthorized access, etc.) and
              other risk management purposes,
            </li>
            <li>
              For our respective business purposes, such as data analysis, usage
              analytics, audits, fraud monitoring and prevention, improvement of
              the Platform, development of new services, identification of usage
              trends, business development, research, etc. For these purposes,
              and in order to pursue our legitimate interests, we may
              de-identify and/or aggregate your personal information with other
              information,
            </li>
            <li>
              For marketing and promotion purposes, in case you have provided us
              with your explicit consent to such processing.
            </li>
          </ol>
          <p>
            We collect, use or share your personal information only when there
            is a valid legal basis allowing us to do so. Most commonly, the
            lawful bases we rely on for processing your personal information
            are:
          </p>
          <ul>
            <li>
              We have your consent. You are able to remove your consent at any
              time. You can do this, by contacting (name of PDO){' '}
              <strong>Andreas Zompanakis</strong>, at the following e-mail
              address:<strong>info@bid2board.com</strong>,
            </li>
            <li>
              We have a legitimate interest, namely for the purposes described
              under section 3 (4), (6), (7), (8), (9), (10), (11),
            </li>
            <li>We have a legal obligation.</li>
          </ul>
          <p>
            bid2board may retain your Personal Information for up to seven (7)
            years from the termination of your right to use the Platform and/or
            the Website, in accordance with the Terms of Use.
          </p>
          <p>
            You acknowledge that bid2board may use your Personal Information as
            set forth in this Privacy Policy in order for you to be able to use
            the Platform and/or the Website. If you don’t wish your Personal
            Information to be used by bid2board as described herein, you may
            send an email to bid2board (contact person: Andreas Zompanakis) at
            the following address info@bid2board.com. Requests are dealt with
            within seven (7) business days. However, please note that any
            objection by you to bid2board’s processing of your Personal
            Information will not affect the lawfulness of the use of your
            Personal Information up to that time and will result in the
            termination of your right to use the Platform and/or Website, in
            accordance with the Terms of Use.
          </p>
          <Heading id="sharing-your-data-with-third-parties">
            <a href="privacy-policy#sharing-your-data-with-third-parties">
              <li>Sharing your data with third parties</li>
            </a>
          </Heading>
          <p>
            bid2board does not generally share your Personal Information with
            any third parties. However, your Personal Information may be
            disclosed in the following circumstances:
          </p>
          <ul>
            <li>By bid2board, for the uses described in the Privacy Policy.</li>
            <li>
              By bid2board to third-party service providers, who provide
              services as website hosting, data analysis, usage analytics,
              business development, payment processing, functionality
              development and improvement, information technology, customer
              support, and related infrastructure provision, provided that such
              service providers have provided bid2board with satisfactory
              assurances of their technical and organizational ability to
              provide appropriate and sufficient data protection, in accordance
              with applicable law.
            </li>
            <li>
              By bid2board to third-party providers of products or services that
              you purchase through the Platform.
            </li>
            <li>
              By bid2board to other users for the implementation of any
              functionality of the Platform.
            </li>
            <li>
              By you to anyone with whom you communicate through the Platform.
            </li>
            <li>
              By you on pages, features, or any services of the Platform to
              which you are able to post information and materials. Please note
              that any information you post or disclose through these services
              may be available to other users as well.
            </li>
            <li>
              By bid2board to a third party in the event of a merger, sale or
              transfer of assets, joint venture, assignment, reorganization or
              bankruptcy or other similar proceedings.
            </li>
            <li>
              By bid2board to comply with any applicable law, including laws
              outside of your country of residence or outside of the European
              Economic Area, to respond to requests from courts, public and/or
              judicial authorities, which may include such authorities outside
              of your country of residence or of the European Economic Area to
              take precautions against liability, to investigate and defend
              bid2board against any third-party claims or allegations.
            </li>
            <li>
              By bid2board to protect the rights, privacy, security, safety,
              integrity, or property of bid2board, facilities, or equipment used
              to make the services available, Platform users or others, and to
              enforce the Agreement.
            </li>
          </ul>
          <p>
            bid2board may transfer to, store and use your Personal Information –
            for troubleshooting, customer support, usage tracking, and data
            storage purposes – in any country where bid2board or bid2board’s
            third-party service providers are based, even countries outside of
            the European Economic Area, and which may have lower levels of data
            protection from those applicable in the European Economic Area.
            bid2board also take steps to protect Personal Information in the
            country in which it is stored, used, or to which it is transferred
            and to ensure that any such data storages, uses, and transfers are
            subject to adequate safeguards. To safeguard such transfers
            bid2board use data protection clauses or other appropriate legal
            mechanisms to safeguard the transfer. If you require more details
            please contact <strong>Andreas Zompanakis</strong> at{' '}
            <strong>info@bid2board.com</strong>.
          </p>
          <Heading id="how-we-proceed-and-store-your-personal-information">
            <a href="privacy-policy#how-we-proceed-and-store-your-personal-information">
              <li>How we proceed and store your personal information</li>
            </a>
          </Heading>
          <p>
            bid2board have put in place appropriate technical, organizational,
            and other measures to protect your Personal Information from
            unauthorized or unlawful use and accidental loss or damage and to
            provide a level of data protection that meets the requirements of
            applicable law. Such measures also apply to entities directly or
            indirectly controlling, controlled by, or under common control with
            bid2board. However, no data security measures are 100% secure. We
            cannot ensure or warrant the security of any information you
            transmit to us or store on the Platform, and you do so at your own
            risk. bid2board suggest that you create a strong password and that
            you periodically change it. Moreover, if you have any reason to
            believe that your interaction with bid2board and/or the Platform
            and/or the Website is no longer secure, please immediately notify
            bid2board at the following address{' '}
            <strong>info@bid2board.com</strong>.
          </p>
          <p>
            You acknowledge and agree that the employees of bid2board may gain
            access to your Personal Information. bid2board shall limit such
            access on a need-to-know basis. bid2board shall take all reasonable
            steps to ensure that such employees are informed of the nature of
            the Personal Information and comply with the provisions of this
            Privacy Policy.
          </p>
          <Heading id="your-data-protection-rights">
            <a href="privacy-policy#your-data-protection-rights">
              <li>Your data protection rights</li>
            </a>
          </Heading>
          <p>Under data protection law, you have the following rights:</p>
          <ul>
            <li>
              Your right to information/communication – You have the right to
              request and receive clear, transparent, and easily understandable
              information regarding the processing of your personal information.
            </li>
            <li>
              Your right of access - You have the right to ask us for copies of
              your personal information in a structured, commonly used, and
              machine-readable format.
            </li>
            <li>
              Your right to rectification - You have the right to ask us to
              rectify personal information you think is inaccurate. You also
              have the right to ask us to complete the information you think is
              incomplete.
            </li>
            <li>
              Your right to erasure/deletion - You have the right to ask us to
              erase or delete your personal information in certain
              circumstances, and especially when it is no longer necessary for
              the purposes collected or when there is no legitimate reason for
              us to continue processing it. The right of deletion is not
              absolute, especially in cases when there is a particular legal
              obligation or other legitimate reason for bid2board to retain your
              personal information.
            </li>
            <li>
              Your right to restriction of processing - You have the right to
              ask us to restrict the processing of your personal information in
              certain circumstances.
            </li>
            <li>
              Your right to object to processing - You have the right to object
              to the processing of your personal information in certain
              circumstances.
            </li>
            <li>
              Your right to data portability - You have the right to ask that we
              transfer the personal information you gave us to another
              organization, or to you, in certain circumstances.
            </li>
            <li>
              Your right to revoke your consent – You have the right to revoke
              your consent at any time, without prejudice to the lawfulness of
              the consent-based processing prior to its revocation, via e-mail
              to: <strong>info@bid2board.com</strong>.
            </li>
          </ul>
          <p>
            You are not required to pay any charge for exercising your rights.
            If you make a request, we have one month to respond to you, starting
            on the date of your submission request.
          </p>
          <p>
            Please contact us at <strong>info@bid2board.com</strong> if you wish
            to make a request.
          </p>
          <Heading id="how-to-complain">
            <a href="privacy-policy#how-to-complain">
              <li>How to complain</li>
            </a>
          </Heading>
          <p>
            If you have any concerns about our use of your personal information,
            you can make a complaint to us at{' '}
            <strong>info@bid2board.com</strong>
          </p>
          <p>
            You can also complain to the relevant competent Data Protection
            Authority if you are unhappy with how we have used your personal
            information.
          </p>
          <p>
            In particular, for complaints regarding the use of your personal
            information by bid2board, you can contact the Hellenic Data
            Protection Authority at the following contact details: (a) Address:
            1-3 Kifissias, 11523, Athens, (b) Telephone: +30 210 6475600 (c)
            Fax: +30 210 6475628 (d) E-mail: <strong>contact@dpa.gr</strong>.
          </p>
          <Heading id="8-cookies-and-similar-technologies">
            <a href="privacy-policy#8-cookies-and-similar-technologies">
              <li>Cookies and similar technologies</li>
            </a>
          </Heading>
          <p>
            The Platform and the Website collect information about the use of
            the Platform and/or the Website. Such information may be collected:
          </p>
          <p>(a) through your browser or device;</p>
          <p>
            (b) through cookies and similar technologies (such as beacons,
            pixels and tags); cookies contain a small amount of information
            downloaded to your computer or other devices by the server of a
            website, page or advertisement, which allow the originating website,
            page or advertisement to recognize your computer or other device on
            subsequent visits; the Platform and the Website use both
            session-based cookies (which last only while your browser is open)
            and persistent cookies (enabling authentication, as well as other
            Platform features, for each time you log on to the Website and/or
            the Platform), as well as third-party cookies, such as Google
            Analytics, subject to the respective service provider’s policies;
            you may opt-out of third-party cookies on that provider’s respective
            website; although most browsers accept cookies by default, you are
            able to set yours to alert you when cookies are being used, as well
            as to accept or reject cookies; you may also opt-out of all or
            certain cookies used by the Website and/or the Platform, but please
            note that such opting-out may significantly disrupt your use of the
            Website and/or Platform and that you may even lose the ability to
            access the Website and/or Platform; cookies can be either associated
            with your account and Personal Information or used to perform
            analytics and personalization, among other things; the following
            types of cookies are used by the Platform and the Website:
          </p>
          <ul>
            <li>
              strictly necessary/essential cookies (i.e. cookies that are
              necessary in order to enable you to browse the Website and use the
              Platform and its features, such as accessing secure areas of the
              Platform),
            </li>
            <li>
              authentication and functionality cookies (i.e. cookies that are
              used to store login information, as well as to provide
              personalized services),
            </li>
            <li>
              performance and analytics cookies (i.e. cookies that are used to
              collect information about the use of the Website and/or the
              Platform, as well as for improvement and research of features and
              services of the Platform),
            </li>
            <li>
              security cookies (i.e. cookies that are used for the detection of
              malware and support of Website and/or Platform security),
            </li>
            <li>
              preference cookies (i.e. cookies that are used for the provision
              of customized services and features),
            </li>
            <li>
              marketing cookies (i.e. cookies that are used for launching and
              monitoring of marketing campaigns, delivering customized
              advertising).
            </li>
          </ul>
          <p>
            (c) through your use of the Platform and/or the Website (including
            but not limited to date and length of visit, location data, traffic
            data, etc.);
          </p>
          <p>
            (d) from you on a voluntary basis, such as information about your
            geographic and/or commercial preferences; and/or
          </p>
          <p>
            (e) by aggregating Personal Information, where such information does
            not personally identify you (collectively,{' '}
            <strong>“Electronic Tools”</strong>).
          </p>
          <p>
            bid2board, and their third-party service providers may use
            information collected through the Electronic Tools for any purpose,
            such as enhancement of navigation, functionality of the Platform,
            monitoring and analyzing the effectiveness of the Website and/or the
            Platform, as well as aggregating usage metrics, except where
            bid2board are required to do otherwise under applicable law
          </p>
          <p>
            If bid2board combine information collected through Electronic Tools
            with Personal Information, it/they will treat the combined
            information as Personal Information.
          </p>
          <Heading id="disabling-cookies">
            <a href="privacy-policy#disabling-cookies">
              <li>Disabling Cookies</li>
            </a>
          </Heading>
          <p>
            You can disable Cookies by accessing the Cookies Control Panel of
            our Website or by changing the relevant settings in your browser.
            Note that the site may not operate correctly if all cookies are
            disabled.
          </p>
          <Heading id="information-about-cookies">
            <a href="privacy-policy#information-about-cookies">
              <li>Information about Cookies</li>
            </a>
          </Heading>
          <p>
            For more information about cookies, how to use them as well as how
            they apply to the use of your personal information, please visit{' '}
            <strong>www.aboutcookies.org</strong>.
          </p>
          <Heading id="other-information-that-we-collect">
            <a href="privacy-policy#other-information-that-we-collect">
              <li>Other information that we collect</li>
            </a>
          </Heading>
          <p>
            IP address is a unique number used by devices to identify and
            consult each other on a computer network using the Internet Protocol
            Standard. The IP address is a number that is assigned to your device
            (PC, tablet, smartphone) each time you access the internet to
            communicate with other devices. The IP address can also be
            recognized by your internet provider.
          </p>
          <p>
            During your visit, the Website records the IP for security reasons
            and for preventing malicious actions. In addition, IP addresses are
            collected in order to analyze the website’s performance and optimize
            it by offering a more user-friendly and functional experience to
            you.
          </p>
          <p>
            This information is necessary for the proper operation of the
            website and the user cannot deactivate them.
          </p>
        </ol>
        <Heading id="website-privacy-policy">
          <a href="privacy-policy#website-privacy-policy">
            Website Privacy Policy
          </a>
        </Heading>
        <p>
          In this Privacy Policy, bid2board PC, a private limited company
          organised and existing under the laws of Greece, whose registered
          office is at 2 Afentouli Street, Piraeus, 18536, Greece, states its
          practices regarding information that it collects from you or through
          your use of the website which is accessible at{' '}
          <strong>https://.bid2board.com</strong> (<strong>“Website”</strong>),
          how that information may be used and with whom it may be shared (the
          <strong>“Privacy Policy”</strong>). The data controllers of your
          Personal Information are bid2board PC of 2 Afentouli Street, Piraeus,
          18536, Greece (<strong>“bid2board”</strong>).
        </p>
        <p>
          The Privacy Policy will be revised from time to time. bid2board will
          notify you of any revisions by posting the updated version of the
          Privacy Policy on the Website. Revised terms become effective on the
          first date that you access or use the Website after the updated
          Privacy Policy has been posted. bid2board advises you to periodically
          check the Privacy Policy for any such modifications or revisions. You
          represent that you have read and understood the Privacy Policy.
        </p>
        <p>
          For purposes of this Privacy Policy, “use” shall mean any operation or
          set of operations performed on information provided by you, such as
          collection, use, storage, forwarding and/or disclosure.{' '}
          <strong>“Process”</strong> shall be construed accordingly.
        </p>
        <Heading id="personal-information">
          <a href="privacy-policy#personal-information">Personal Information</a>
        </Heading>
        <p>
          <strong>“Personal Information”</strong> refers to information that
          allows bid2board to identify you, directly or indirectly, in
          particular by reference to an identifier, in the course of your
          accessing and using the Website. Personal information shall be
          provided by you in case you elect to contact bid2board via the contact
          forms available on the Website for requesting a demo and/or
          information on bid2board’s product(s) and/or services or any other
          matter; it includes name, address, email, telephone number,
          company/employer, job function, team size
        </p>
        <p>
          bid2board may also collect Personal Information automatically through
          your use of the Website, such as IP address, date and daytime of
          access request, time zone difference to Greenwich Mean Time (GMT),
          specific site requested, HTTP-status code, amount of data transmitted,
          the website from which you visit the Website, browser type, operating
          system and user interface, language and version of browser software.
          Please refer to the section on Cookies and similar technologies below
          for more information.
        </p>
        <p>b) bid2board may use your Personal Information to:</p>
        <ul>
          <li>Improve your interaction with the Website.</li>
          <li>Notify you of any changes to the Website.</li>
          <li>
            Communicate with you with respect to bid2board’s services and
            products.
          </li>
          <li>Enforce the Terms of Use and/or Privacy Policy.</li>
          <li>
            Send you marketing and remarketing communication, unless you have
            requested by written notice that your Personal Information not be
            used for the purposes of marketing and remarketing.
          </li>
          <li>
            Request your participation in surveys related to the Website, its
            services and bid2board’s products and/or services and administer
            such surveys.
          </li>
          <li>
            For bid2board’s business purposes, such as data analysis, usage
            analytics, audits, fraud monitoring and prevention, development of
            new products and services, improvement of products and services,
            identification of usage trends, business development, research etc.
            For these purposes, bid2board may de-identify, and/or aggregate with
            other information, your Personal Information.{' '}
          </li>
        </ul>
        <p>
          c) bid2board does not generally share your Personal Information with
          any third parties. However, your Personal Information may be disclosed
          in the following circumstances:
        </p>
        <ul>
          <li>
            By bid2board to third-party service providers, who provide services
            to bid2board such as website hosting, data analysis, usage
            analytics, business development, functionality development and
            improvement, information technology, customer support and related
            infrastructure provision, provided that such service providers have
            provided bid2board with satisfactory assurances of their technical
            and organizational ability to provide appropriate and sufficient
            data protection, in accordance with applicable law.
          </li>
          <li>
            By bid2board to a third party in the event of a merger, sale or
            transfer of assets, joint venture, assignment, reorganization or
            bankruptcy or other similar proceedings.
          </li>
          <li>
            By bid2board to comply with any applicable law, including laws
            outside of your country of residence or outside of the European
            Economic Area, to respond to requests from courts, public and/or
            judicial authorities, which may include such authorities outside of
            your country of residence or of the European Economic Area to take
            precautions against liability, to investigate and defend bid2board
            against any third-party claims or allegations.
          </li>
          <li>
            By bid2board to protect the rights, privacy, security, safety,
            integrity or property of bid2board, facilities or equipment used to
            make the Website available, or others and to enforce the Terms of
            Use and/or Privacy Policy.
          </li>
        </ul>
        <p>
          d) bid2board may retain your Personal Information for up to 5 years
          from its collection or submission.
        </p>
        <p>
          e) bid2board may transfer to, store and use your Personal Information
          – for troubleshooting, customer support, usage tracking and data
          storage purposes – in any country where bid2board’s third-party
          service providers are based, even countries outside of the European
          Economic Area, and which may have lower levels of data protection from
          those applicable in the European Economic Area. bid2board also takes
          steps to protect Personal Information in the country in which it is
          stored, used or to which it is transferred and to ensure that any such
          data storages, uses and transfers are subject to adequate safeguards.
        </p>
        <p>
          f) You acknowledge that bid2board may use your Personal Information as
          provided for in this Privacy Policy in order for you to be able to use
          the Website. If you don’t wish your Personal Information to be used by
          bid2board as described herein, you may send an email to bid2board
          (contact person: <strong>Andreas Zompanakis</strong>) at the following
          address <strong>info@bid2board.com</strong> Requests are dealt with
          within seven (7) business days. However, please note that any
          objection by you to bid2board’s processing of your Personal
          Information will not affect the lawfulness of the use of your Personal
          Information up to that time.
        </p>
        <p>
          g) bid2board has put in place appropriate technical, organizational
          and other measures to protect your Personal Information from
          unauthorized or unlawful use and accidental loss or damage and to
          provide a level of data protection that meets the requirements of
          applicable law. Such measures also apply to entities directly or
          indirectly controlling, controlled by or under common control with
          bid2board. However, no data security measures are 100% secure. We
          cannot ensure or warrant the security of any information you transmit
          to us, and you do so at your own risk. Moreover, if you have reason to
          believe that your interaction with the Website is no longer secure,
          please immediately notify bid2board at the following address{' '}
          <strong>info@bid2board.com</strong>.
        </p>
        <p>
          h) You have the right to obtain from bid2board confirmation as to
          whether or not personal data concerning you are being processed and,
          if that is the case, you have the right to request access to your
          personal data and to information regarding their processing, such as
          the recipients of your personal data, the time period for which your
          personal data will be stored etc. You have the right to request a copy
          of your personal data under processing, to request the rectification
          of inaccurate personal data concerning you, to request the completion
          of incomplete personal data concerning you, to request the erasure of
          your personal data, to request the restriction of processing of your
          personal data, to receive the personal data concerning you in a
          structured, commonly used and machine-readable format, to transmit
          your personal data to another data controller, without hindrance from
          bid2board, to request the direct transmission of your personal data
          from bid2board to another data controller, where technically feasible,
          to lodge a complaint with the competent Data Protection Authority, to
          object at any time to the processing of your personal data, to object
          at any time to the processing of your personal data for direct
          marketing purposes.
        </p>
        <ul>
          <p>
            i) You acknowledge and agree that all information you submit is
            truthful and accurate.
          </p>
        </ul>
        <p>
          k) You shall not provide any Personal Information about others unless
          you are authorized to do so. By submitting Personal Information about
          others, you represent and warrant that you are authorized to do so and
          that you have received authorization from the person about whom you
          provide Personal Information and, where required, that that person has
          consented to having all such Personal Information submitted in
          accordance with this Privacy Policy.
        </p>
        <p>
          You acknowledge and agree that the employees of bid2board may gain
          access to your Personal Information. bid2board shall limit such access
          on a need-to-know basis. bid2board shall take all reasonable steps to
          ensure that such employees are informed of the nature of the Personal
          Information and comply with the provisions of this Privacy Policy.
        </p>
        <p>
          You may contact <strong>Andreas Zompanakis</strong> at the following
          address <strong>info@bid2board.com</strong> for any questions about
          the Privacy Policy or any complaints or objections about the way your
          Personal Information has been processed by bid2board.
        </p>
        <p>
          l) The Website may include links to third-party websites or
          applications whose privacy policies may differ from ours. Any
          information that these third parties collect about you is governed by
          their respective privacy policies. We suggest that you contact those
          third parties directly in order to obtain information about their
          privacy policies.
        </p>
        <Heading id="cookies-and-similar-technologies">
          <a href="privacy-policy#cookies-and-similar-technologies">
            Cookies and similar technologies
          </a>
        </Heading>
        <p>
          a) The Website collects information about the use of the Website. Such
          information may be collected:
        </p>
        <ul>
          <p>(i) through your browser or device;</p>
          <p>
            (ii) through cookies and similar technologies (such as beacons,
            pixels and tags); cookies contain a small amount of information
            downloaded to your computer or other device by the server of a
            website, page or advertisement, which allow the originating website,
            page or advertisement to recognize your computer or other device on
            subsequent visits; the Website uses both session-based cookies
            (which last only while your browser is open) and persistent cookies
            (enabling authentication, as well as other features, for each time
            you log on to the Website ), as well as third-party cookies, such as
            Google Analytics, Google Ads remarketing, Microsoft Azure,
            DoubleClick, subject to the respective service provider’s policies;
            you may opt-out of third-party cookies on that provider’s respective
            website; although most browsers accept cookies by default, you are
            able to set yours to alert you when cookies are being used, as well
            as to accept or reject cookies; you may also opt-out of all or
            certain cookies used by the Website , but please note that such
            opting-out may significantly disrupt your use of the Website and
            that you may even lose the ability to access the Website;
          </p>
          <p>
            (iii) through your use of the Website (including but not limited to
            date and length of visit, location data, traffic data etc.);
          </p>
          <p>
            (iv) from you on a voluntary basis, such as information about your
            geographic and/or commercial preferences; and/or
          </p>
          <p>
            (v) by aggregating Personal Information, where such information does
            not personally identify you (collectively,{' '}
            <strong>“Electronic Tools”</strong>).
          </p>
        </ul>
        <p>
          b) bid2board and their third-party service providers may use
          information collected through the Electronic Tools for any purpose,
          such as enhancement of navigation, functionality of the Website,
          monitoring and analyzing the effectiveness of the Website, as well as
          aggregating usage metrics, except where bid2board is required to do
          otherwise under applicable law.
        </p>
        <p>
          c) If bid2board combines information collected through Electronic
          Tools with Personal Information, it will treat the combined
          information as Personal Information.
        </p>
      </Wrapper>
    </Layout>
  );
};

export default PrivacyPolicy;
